
import { Sinister } from '../../../shared/types/sinisters/sinisterInfo';
import Vue from 'vue'
import { Component, Inject, Mixins, Watch, Prop } from 'vue-property-decorator';
import Button from '../../../core/components/Generics/Button.vue'
import SimpleTable from '../../../core/components/Generics/Table/SwTable/SimpleTable.vue'
import { sortTypes, TablePaginatedFilters, ClaimTableFilters } from '../../../core/components/Generics/Table/SwTable/Table';
import { NAME_ROUTES } from '../../../core/utils';
import Axios, { CancelTokenSource } from 'axios';
import { ClaimManagementPort } from '../../../core/api/ports';
import { InjectionKey } from '../../../core/injection-keys';
import { formatDate } from '../../../core/utils/date-handlers';
import SinisterFilters from './filters/SinisterFilters.vue';
import { UserInfo } from '../../../core/auth/services/auth.service';
import {
    customerName,
    CustomerOption,
    customerOptions,
    showCustomerColumn,
    stripUnsortableSort
} from '../claims-list-visibility';

const CLAIMS_LIST_FILTERS_KEY = 'claims_sinisters_list_filters_v1';

@Component({
    components: {
        SimpleTable,
        SinisterFilters,
        Button
    }
})

export default class SinistersList extends Vue {
    @Inject(InjectionKey.ClaimManagementApi) readonly claimsApi!: ClaimManagementPort;
    itemsList: Array<Sinister> = [];
    searchRanchList: string = '';
    headers: Array<object> = [];
    pendingItems: boolean = true;

    // Se arma en `created()` y al cambiar la visibilidad de la columna Aseguradora (regla 8: no en el inicializador).
    private buildHeaders(): Array<object> {
        const headers: Array<object> = [
            {
                text: 'Siniestro',
                value: 'claim_id',
                sortable: true
            }
        ];
        if (this.showCustomerColumn) {
            headers.push({
                text: 'Aseguradora',
                value: 'customer_name',
                sortable: false
            });
        }
        headers.push(
            {
                text: 'Tipo de evento',
                value: 'event_type',
                sortable: true
            },
            {
                text: 'Fecha del evento',
                value: 'event_date',
                sortable: true,
                formatter: (value: string) => formatDate(value, false, true)
            },
            {
                text: 'Área',
                value: 'area',
                sortable: true
            },
            {
                text: 'Acciones',
                value: 'actions',
                align: 'center',
                sortable: false
            }
        );
        return headers;
    }

    //Paginacion
    private static readonly DEFAULT_PAGE_SIZE = 50;
    requestTokenInstance: CancelTokenSource = Axios.CancelToken.source();
    paginationTotalPages: number = 0;
    paginationTotalRows: number = 0;
    searchParams: TablePaginatedFilters = {
        page: 1,
        page_size: SinistersList.DEFAULT_PAGE_SIZE,
    };
    claimsTableOptions: object = {
        itemsPerPage: SinistersList.DEFAULT_PAGE_SIZE,
    };
    appraisersOptions: Array<object> = [];
    pendingAppraisers: boolean = false;

    private initialListLoadDone: boolean = false;
    private persistEnabled: boolean = false;
    private persistDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    boundOnWindowFocus = () => {
        this.onWindowFocusFromStorage();
    };

    created() {
        this.headers = this.buildHeaders();
        this.applyPersistedSearchParamsFromStorage();
        this.findAppraisers();
    }

    get userInfo(): UserInfo | null {
        return this.$store.getters['auth/userInfo'] || null;
    }

    get customersOptions(): CustomerOption[] {
        return customerOptions(this.userInfo);
    }

    // Reglas por rol en `claims-list-visibility.ts` (Smartway y tasadores la ven; auditores no).
    get showCustomerColumn(): boolean {
        return showCustomerColumn(this.userInfo);
    }

    customerTranslator(id: number | null | undefined): string {
        return customerName(this.userInfo ? this.userInfo.customers : undefined, id);
    }

    @Watch('showCustomerColumn')
    onShowCustomerColumnChanged() {
        this.headers = this.buildHeaders();
    }

    // `/me/` se refresca al entrar (`PageClaimsList`): si llega después del listado, se vuelven a traducir los nombres
    @Watch('userInfo')
    onUserInfoChanged() {
        this.itemsList = this.itemsList.map(item => ({
            ...item,
            customer_name: this.customerTranslator(item.ranch && item.ranch.customer)
        }));
    }

    mounted() {
        window.addEventListener('focus', this.boundOnWindowFocus);
        this.$nextTick(() => {
            this.tryCompleteInitialListLoad();
        });
    }

    beforeDestroy() {
        window.removeEventListener('focus', this.boundOnWindowFocus);
        if (this.persistDebounceTimer != null) {
            clearTimeout(this.persistDebounceTimer);
            this.persistDebounceTimer = null;
        }
        // Cancel any pending request when component is destroyed
        if (this.requestTokenInstance) {
            this.requestTokenInstance.cancel('Component destroyed');
        }
    }

    seeClaimDetail(item: any) {
        if (item && item.id) {
            // Flush sort/filters to storage before opening detail tab so focus-sync cannot restore a stale snapshot.
            this.flushPersistedSearchParams();
            const route = this.$router.resolve({
                name: 'claimDetail',
                params: { id: item.id.toString() },
            });
            window.open(route.href, '_blank', 'noopener,noreferrer');
        }
    }

    get tableSortBy(): string[] {
        return this.searchParams.sort_by ? [...this.searchParams.sort_by] : [];
    }

    get tableSortDesc(): boolean[] {
        if (!this.searchParams.sort_by || this.searchParams.sort_by.length === 0) {
            return [];
        }
        return [this.searchParams.sort_type === sortTypes.desc];
    }

    private syncClaimsTableOptionsFromSearchParams() {
        const pageSize = this.searchParams.page_size || SinistersList.DEFAULT_PAGE_SIZE;
        if (this.claimsTableOptions['itemsPerPage'] !== pageSize) {
            this.claimsTableOptions = { itemsPerPage: pageSize };
        }
    }

    private hasActiveSort(params: TablePaginatedFilters = this.searchParams): boolean {
        return !!(params.sort_by && params.sort_by.length > 0);
    }

    private flushPersistedSearchParams() {
        if (this.persistDebounceTimer != null) {
            clearTimeout(this.persistDebounceTimer);
            this.persistDebounceTimer = null;
        }
        this.writePersistedSearchParams();
    }

    private normalizeStoredSearchParams(s: any): ClaimTableFilters | null {
        if (!s || typeof s !== 'object') {
            return null;
        }
        const next: ClaimTableFilters = {
            page: typeof s.page === 'number' && s.page >= 1 ? s.page : 1,
            page_size: typeof s.page_size === 'number' && s.page_size > 0 ? s.page_size : SinistersList.DEFAULT_PAGE_SIZE,
        };
        if (s.query !== undefined && s.query !== null && String(s.query).trim() !== '') {
            next.query = String(s.query).trim();
        }
        if (s.event_type !== undefined && s.event_type !== null && String(s.event_type) !== '') {
            next.event_type = Number(s.event_type);
        }
        if (s.start_date && String(s.start_date) !== '') {
            next.start_date = String(s.start_date);
        }
        if (s.end_date && String(s.end_date) !== '') {
            next.end_date = String(s.end_date);
        }
        if (s.appraisers && String(s.appraisers).trim() !== '') {
            next.appraisers = String(s.appraisers).trim();
        }
        if (s.customers && String(s.customers).trim() !== '') {
            next.customers = String(s.customers).trim();
        }
        if (s.sort_by !== undefined && s.sort_by !== null) {
            next.sort_by = Array.isArray(s.sort_by) ? [...s.sort_by] : s.sort_by;
        }
        if (s.sort_type) {
            next.sort_type = s.sort_type;
        }
        return next;
    }

    private applyPersistedSearchParamsFromStorage() {
        try {
            const raw = localStorage.getItem(CLAIMS_LIST_FILTERS_KEY);
            if (!raw) {
                return;
            }
            const parsed = JSON.parse(raw);
            if (parsed.v !== 1) {
                return;
            }
            const normalized = this.normalizeStoredSearchParams(parsed.searchParams);
            if (normalized) {
                this.searchParams = normalized;
                this.syncClaimsTableOptionsFromSearchParams();
            }
        } catch (_) {
            /* ignore invalid snapshot */
        }
    }

    private readPersistedSearchParams(): ClaimTableFilters | null {
        try {
            const raw = localStorage.getItem(CLAIMS_LIST_FILTERS_KEY);
            if (!raw) {
                return null;
            }
            const parsed = JSON.parse(raw);
            if (parsed.v !== 1) {
                return null;
            }
            return this.normalizeStoredSearchParams(parsed.searchParams);
        } catch (_e) {
            return null;
        }
    }

    private filterFingerprint(p: TablePaginatedFilters): string {
        const c = p as ClaimTableFilters;
        return JSON.stringify({
            q: p.query !== undefined && p.query !== null ? String(p.query) : '',
            et: c.event_type !== undefined && c.event_type !== null ? c.event_type : null,
            ap: c.appraisers !== undefined && c.appraisers !== null ? String(c.appraisers) : '',
            cu: c.customers !== undefined && c.customers !== null ? String(c.customers) : '',
            sd: c.start_date !== undefined && c.start_date !== null ? String(c.start_date) : '',
            ed: c.end_date !== undefined && c.end_date !== null ? String(c.end_date) : '',
            page: p.page,
            ps: p.page_size,
            sb: JSON.stringify(p.sort_by || []),
            st: p.sort_type !== undefined && p.sort_type !== null ? p.sort_type : null,
        });
    }

    private onWindowFocusFromStorage() {
        if (this.$route.name !== NAME_ROUTES.CLAIMS_LIST.name) {
            return;
        }
        if (!this.initialListLoadDone) {
            return;
        }
        const remote = this.readPersistedSearchParams();
        if (!remote) {
            return;
        }
        if (this.filterFingerprint(remote) === this.filterFingerprint(this.searchParams)) {
            return;
        }
        if (this.hasActiveSort(this.searchParams) && !this.hasActiveSort(remote)) {
            this.flushPersistedSearchParams();
            return;
        }
        this.searchParams = remote;
        this.syncClaimsTableOptionsFromSearchParams();
        const filters = this.$refs.sinisterFilters as Vue & { restoreUIFromSearchParams: (p: ClaimTableFilters) => void };
        if (filters) {
            filters.restoreUIFromSearchParams(this.searchParams as ClaimTableFilters);
        }
        this.findRanchs();
    }

    private tryCompleteInitialListLoad() {
        if (this.initialListLoadDone) {
            return;
        }
        if (this.pendingAppraisers) {
            return;
        }
        const filters = this.$refs.sinisterFilters as (Vue & { restoreUIFromSearchParams: (p: ClaimTableFilters) => void }) | undefined;
        if (!filters) {
            return;
        }
        filters.restoreUIFromSearchParams(this.searchParams as ClaimTableFilters);
        this.initialListLoadDone = true;
        this.findRanchs();
        this.$nextTick(() => {
            this.persistEnabled = true;
        });
    }

    private writePersistedSearchParams() {
        try {
            localStorage.setItem(
                CLAIMS_LIST_FILTERS_KEY,
                JSON.stringify({ v: 1, searchParams: this.searchParams })
            );
        } catch (_) {
            /* ignore quota / private mode */
        }
    }

    @Watch('searchParams', { deep: true })
    schedulePersistSearchParams() {
        if (!this.persistEnabled) {
            return;
        }
        if (this.persistDebounceTimer != null) {
            clearTimeout(this.persistDebounceTimer);
        }
        this.persistDebounceTimer = setTimeout(() => {
            this.persistDebounceTimer = null;
            this.writePersistedSearchParams();
        }, 250);
    }

    @Watch('pendingAppraisers')
    onPendingAppraisersChanged(pending: boolean) {
        if (pending) {
            return;
        }
        this.$nextTick(() => {
            this.tryCompleteInitialListLoad();
        });
    }

    

    findRanchs(){
        this.getRanchList();
    }

    findAppraisers(){
        this.pendingAppraisers = true;
        this.appraisersOptions = [];
        this.claimsApi.getAppraisersList()
        .then((res) => {
            this.appraisersOptions = res.appraisers || [];
        })
        .finally(() => {
            this.pendingAppraisers = false;
        })
    }



    getRanchList(){
        // Cancel previous request if exists
        this.requestTokenInstance.cancel('Request cancelled due to new search');
        // Create new cancel token for this request
        this.requestTokenInstance = Axios.CancelToken.source();
        
        this.searchingItems(true);
        this.claimsApi.getClaimsList(
            this.requestTokenInstance, 
            // La columna Aseguradora no se ordena en backend: nunca se manda en `sort_by`
            stripUnsortableSort(this.searchParams)
        )
        .then(res => {
            this.paginationTotalPages = res.totalPages;
            this.paginationTotalRows = res.count;
            this.setTableItems(res.data);
            this.searchingItems(false);
        })
        .catch(err => {
            // Don't update UI if request was cancelled
            if (Axios.isCancel(err)) {
                return;
            }
            
            this.paginationTotalPages = 1;
            this.paginationTotalRows = 0;
            this.setTableItems([]);
            this.searchingItems(false);
            console.error('Error loading claims list:', err);
        })
    }

    get propertiesData() {
        return this.$store.state.properties && this.$store.state.properties.properties || null;
    }

    get eventTypeTranslator() {
        return this.$store.getters['properties/getEventTypeName'];
    }

    getEventTypeName(eventType: number): string {
        if (this.eventTypeTranslator) {
            return this.eventTypeTranslator(eventType);
        }
        return eventType && eventType.toString() || '';
    }

    setTableItems(items) {
        // Translate event types and customer for display
        this.itemsList = items.map(item => ({
            ...item,
            event_type: this.getEventTypeName(item.event_type),
            customer_name: this.customerTranslator(item.ranch && item.ranch.customer)
        }));
    }

    searchingItems(value) {
        this.pendingItems = value;
    }


    sortHeaders(headers) {
        this.headers = headers;
    }

    searchAction(params:TablePaginatedFilters){
        let pageSize = this.searchParams.page_size;
        
        // Create new search params
        const newParams: TablePaginatedFilters = {
            page: 1,
            page_size: pageSize,
        };

        // Preserve sort parameters if they exist
        if (this.searchParams.sort_by) {
            newParams.sort_by = this.searchParams.sort_by;
        }
        if (this.searchParams.sort_type) {
            newParams.sort_type = this.searchParams.sort_type;
        }

        // Add new filter params (this will overwrite old values or add new ones)
        Object.keys(params).forEach(key => {
            if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
                newParams[key] = params[key];
            }
        });

        this.searchParams = newParams;
        this.findRanchs();
    }

     updateTableOptions({sortBy, sortDesc, itemsPerPage, page}){
        const incomingEmpty = !sortBy || sortBy.length === 0;
        this.searchParams.page_size = itemsPerPage;
        this.searchParams.page = page;
        this.syncClaimsTableOptionsFromSearchParams();
        if (incomingEmpty) {
            delete this.searchParams.sort_by;
            delete this.searchParams.sort_type;
        } else {
            const sortType = sortDesc[0] === true ? sortTypes.desc : sortTypes.asc;
            this.searchParams.sort_by = sortBy;
            this.searchParams.sort_type = sortType;
        }
        this.findRanchs();
        if (this.persistEnabled) {
            this.flushPersistedSearchParams();
        }
    }

    updatesearchParams(page, itemsPerPage){
        this.searchParams.page = page;
        this.searchParams.page_size = itemsPerPage;
        this.syncClaimsTableOptionsFromSearchParams();
        this.findRanchs();
    }

}

